/* TechnicianVisits — iter97.88 (Phase A3)
 *
 * The full queue of visits assigned to the logged-in technician. Two
 * tabs: Active (status=assigned) and Completed (status=completed).
 * Click a row to drill into TechnicianVisitDetail and capture scans.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass, Calendar, MapPin, Stethoscope, User, CheckCircle, Clock } from "@phosphor-icons/react";

const TABS = [
    { id: "assigned",  label: "Active" },
    { id: "completed", label: "Completed" },
];

function relativeDateLabel(dateStr) {
    if (!dateStr) return "";
    try {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const d = new Date(dateStr + "T00:00:00");
        const diff = Math.round((d - today) / (24 * 60 * 60 * 1000));
        if (diff === 0) return "Today";
        if (diff === 1) return "Tomorrow";
        if (diff === -1) return "Yesterday";
        if (diff > 1) return `in ${diff} days`;
        return `${Math.abs(diff)} days ago`;
    } catch { return ""; }
}

export default function TechnicianVisits() {
    const [tab, setTab] = useState("assigned");
    const [visits, setVisits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");

    const load = (status) => {
        setLoading(true);
        api.get("/coe/technician/visits", { params: { status } })
            .then((r) => setVisits(r.data || []))
            .catch(() => setVisits([]))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(tab); }, [tab]);

    const filtered = useMemo(() => {
        if (!q) return visits;
        const l = q.toLowerCase();
        return visits.filter((v) =>
            (v.patient_name || "").toLowerCase().includes(l) ||
            (v.doctor_name || "").toLowerCase().includes(l) ||
            (v.doctor_clinic_name || "").toLowerCase().includes(l) ||
            (v.patient_phone || "").includes(q),
        );
    }, [visits, q]);

    return (
        <div data-testid="technician-visits-page">
            <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
                <div>
                    <h1 className="font-display text-3xl font-bold">My visits</h1>
                    <p className="text-muted-foreground text-sm mt-1">
                        Every scan visit admin has routed to you.
                    </p>
                </div>
                <div className="relative w-72">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        placeholder="Search patient, doctor, clinic…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="pl-9 h-9"
                        data-testid="technician-visits-search"
                    />
                </div>
            </div>

            <div className="flex items-center gap-2 mb-4 border-b border-border">
                {TABS.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        onClick={() => setTab(t.id)}
                        className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors -mb-px ${
                            tab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
                        }`}
                        data-testid={`technician-visits-tab-${t.id}`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {loading && <div className="text-muted-foreground text-sm">Loading…</div>}

            {!loading && filtered.length === 0 && (
                <div className="bg-card border border-border rounded-2xl p-10 text-center text-muted-foreground" data-testid="technician-visits-empty">
                    <Calendar size={32} className="mx-auto mb-2 opacity-50" />
                    {tab === "assigned" ? "No active visits assigned to you right now." : "No completed visits yet."}
                </div>
            )}

            {!loading && filtered.length > 0 && (
                <div className="space-y-2">
                    {filtered.map((v) => (
                        <Link
                            key={v.id}
                            to={`/coe/technician/visits/${v.id}`}
                            className="bg-card border border-border rounded-2xl p-4 flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-6 hover:border-primary/40 transition-colors"
                            data-testid={`technician-visit-row-${v.id}`}
                        >
                            <div className="flex items-center gap-3 min-w-[180px]">
                                <div className="w-12 h-12 rounded-xl bg-primary/10 flex flex-col items-center justify-center text-primary shrink-0">
                                    <Calendar size={18} weight="bold" />
                                </div>
                                <div>
                                    <div className="font-semibold text-sm">{v.date} · {v.time}</div>
                                    <div className="text-[11px] text-muted-foreground">{relativeDateLabel(v.date)}</div>
                                </div>
                            </div>

                            <div className="flex items-center gap-2 min-w-[200px]">
                                <User size={14} className="text-muted-foreground shrink-0" />
                                <div>
                                    <div className="text-sm font-medium">{v.patient_name || "—"}</div>
                                    <div className="text-[11px] text-muted-foreground">{v.patient_phone || ""}</div>
                                </div>
                            </div>

                            <div className="flex items-center gap-2 min-w-[220px]">
                                <Stethoscope size={14} className="text-muted-foreground shrink-0" />
                                <div>
                                    <div className="text-sm font-medium">{v.doctor_name || "—"}</div>
                                    <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                                        <MapPin size={11} /> {v.doctor_clinic_name || "—"}
                                    </div>
                                </div>
                            </div>

                            <div className="lg:ml-auto">
                                {v.status === "completed" ? (
                                    <span className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 font-semibold">
                                        <CheckCircle size={11} weight="fill" /> Completed
                                    </span>
                                ) : (
                                    <span className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 text-primary font-semibold">
                                        <Clock size={11} weight="fill" /> Assigned
                                    </span>
                                )}
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}
